import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import { Form, Dropdown, TextArea } from 'components/HeroSemantic';
import get from '@htmniseko/htm-helpers/misc/get';
import momentJpn from '@htmniseko/htm-helpers/time/momentJpn';
import { DateRangePicker } from 'components/DatePicker/DateInputs';
import { ThemeButton } from 'components';
import HeroSelectField from 'components/Field/HeroSelectField';

const CLEANING_OPTIONS = [
  'cleaningOptions.eco',
  'cleaningOptions.fullServiced',
  'cleaningOptions.other',
];
const PAYMENT_OPTIONS = [
  'paymentOptions.ownerAccount',
  'paymentOptions.cashCreditCard',
];
const STORAGE_KEY_OPTIONS = [
  'storageKeyOptions.yes',
  'storageKeyOptions.no',
];
const SERVICE_OPTIONS = [
  'serviceOptions.transfers',
  'serviceOptions.liftPasses',
  'serviceOptions.rentals',
  'serviceOptions.lessons',
  'serviceOptions.carRentals',
];

const labelClass = 'text-[11px] font-semibold leading-4 tracking-[0.04em] text-[var(--owner-text-muted)] uppercase';
const noteClass = 'mt-0.5 text-[12px] font-normal leading-4 text-[var(--owner-text-muted)]';
const errorTextClass = 'mt-1 text-[12px] leading-4 text-[var(--owner-danger)]';
const inputClass = [
  'min-h-10 w-full rounded-lg border bg-[var(--owner-surface)] px-3 text-sm leading-5',
  'text-[var(--owner-text)] shadow-sm outline-none transition-colors',
  'focus:border-[var(--tenant-primary)] focus:ring-2 focus:ring-[var(--owner-focus-ring)]',
].join(' ');

const errorInputClass = 'border-[var(--owner-danger)]';
const restingInputClass = 'border-[var(--owner-border-strong)]';

const submitButtonStyle = {
  background: 'color-mix(in srgb, var(--tenant-primary) 78%, #000 22%)',
  border: 'none',
  borderRadius: '8px',
  color: '#FFFFFF',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '20px',
  minWidth: '120px',
  padding: '10px 20px',
};

const backButtonStyle = {
  background: 'var(--owner-surface)',
  border: '1px solid var(--owner-border-strong)',
  borderRadius: '8px',
  color: 'var(--owner-text)',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '20px',
  minWidth: '120px',
  padding: '10px 20px',
};

function InlineError({ visible }) {
  if (!visible) {return null;}
  return (
    <span className={errorTextClass}>
      <FormattedMessage id="form.error.required.message" />
    </span>
  );
}

function FieldCell({ labelId, label, required, note, error, children, hasError = false, fullWidth }) {
  const { formatMessage, messages } = useIntl();
  const className = fullWidth ? 'flex h-full flex-col gap-1.5 md:col-span-2' : 'flex h-full flex-col gap-1.5';
  // React Intl treats defaultMessage: '' as absent, so a missing translation falls back to its key.
  const noteText = note && messages[note] ? formatMessage({ id: note }) : null;
  return (
    <div className={className}>
      <div>
        <span className={labelClass}>
          {label || formatMessage({ id: labelId })}
          {required && <span style={{ color: 'var(--owner-danger)', marginLeft: '2px' }}>*</span>}
        </span>
        {noteText && (
          <div className={noteClass}>
            {noteText}
          </div>
        )}
      </div>
      <div className="mt-auto">{children}</div>
      <InlineError visible={hasError} />
      {error}
    </div>
  );
}

function DropdownField({ keyName, labelId, options, required, multiple, note, placeholder, data, editable, hasError, onChange }) {
  const { formatMessage } = useIntl();
  let value = get(data, keyName, multiple ? [] : '');
  if (multiple && !editable) {
    value = (get(data, keyName, []) || []).join(', ');
  }
  const resolvedPlaceholder = placeholder ||
    formatMessage({ id: 'form.placeholder.select', defaultMessage: 'Select…' });
  // For multi-select, swap the native-select-backed Dropdown for the
  // chip-and-checkbox popover field — same look as the rest of the form
  // but with a real multi-select UX.
  let control;
  if (!editable) {
    control = <span style={{ color: 'var(--owner-text)', fontSize: '14px', lineHeight: '22px' }}>{value}</span>;
  }
  else if (multiple) {
    control = (
      <HeroSelectField
        multiple
        ariaLabel={formatMessage({ id: `form.label.${labelId || keyName}`, defaultMessage: labelId || keyName })}
        options={options}
        value={value}
        placeholder={resolvedPlaceholder}
        className={hasError(keyName) ? errorInputClass : ''}
        onChange={nextValue => onChange(keyName, nextValue)}
      />
    );
  }
  else {
    control = (
      <Dropdown
        fluid
        selection
        name={keyName}
        ariaLabel={formatMessage({ id: `form.label.${labelId || keyName}`, defaultMessage: labelId || keyName })}
        options={options}
        value={value}
        placeholder={resolvedPlaceholder}
        className={`${inputClass} ${hasError(keyName) ? errorInputClass : restingInputClass}`}
        onChange={(event, eventData) => onChange(keyName, eventData.value)}
      />
    );
  }
  return (
    <FieldCell labelId={`form.label.${labelId || keyName}`} required={required} note={note} hasError={hasError(keyName)}>
      {control}
    </FieldCell>
  );
}

function PaxInput({ paxKey, data, editable, hasError, onChange }) {
  const { formatMessage } = useIntl();
  const errKey = `pax.${paxKey}`;
  const hasErr = hasError(errKey);
  return (
    <div className="flex h-full flex-col gap-1.5">
      <span className={labelClass}>{formatMessage({ id: `form.label.${paxKey}` })}</span>
      <input
        type="number"
        min="0"
        className={`mt-auto ${inputClass} ${hasErr ? errorInputClass : restingInputClass}`}
        value={get(data, errKey, '')}
        onChange={event => onChange(errKey, event.target.value)}
        disabled={!editable}
      />
    </div>
  );
}

function BeddingRoom({ room, options, onChange }) {
  const { formatMessage } = useIntl();
  const label = `${formatMessage({ id: 'form.label.bedConfiguration', defaultMessage: 'Bed Configuration' })} — ${room.code}`;
  return (
    <FieldCell
      label={label}
      error={room.bedConfigs.length === 0 && (
        <span className={noteClass}>
          <FormattedMessage
            id="form.requestBooking.beddingUnavailable"
            defaultMessage="Bedding options are currently unavailable for this room. You can decide later."
          />
        </span>
      )}
    >
      <Dropdown
        fluid
        ariaLabel={label}
        name={`bedding.${room.pmsRoomId}`}
        className={`${inputClass} ${restingInputClass}`}
        options={options}
        value={room.value}
        onChange={(event, eventData) => onChange(`bedding.${room.pmsRoomId}`, eventData.value)}
      />
    </FieldCell>
  );
}

function GuestsField({ data, editable, hasError, onChange }) {
  const { formatMessage } = useIntl();
  const anyPaxError = ['adults', 'children', 'infants'].some(k => hasError(`pax.${k}`));
  return (
    <div className="flex h-full flex-col gap-2">
      <span className={labelClass}>
        {formatMessage({ id: 'form.label.guests' })}
        <span style={{ color: 'var(--owner-danger)', marginLeft: '2px' }}>*</span>
      </span>
      <div className="mt-auto grid grid-cols-3 gap-3">
        <PaxInput paxKey="adults" data={data} editable={editable} hasError={hasError} onChange={onChange} />
        <PaxInput paxKey="children" data={data} editable={editable} hasError={hasError} onChange={onChange} />
        <PaxInput paxKey="infants" data={data} editable={editable} hasError={hasError} onChange={onChange} />
      </div>
      {anyPaxError && (
        <span className={errorTextClass}>
          <FormattedMessage id="form.error.required.message" />
        </span>
      )}
    </div>
  );
}

function DatesField({ data, errors, submitAttempted, editable, onChange }) {
  const hasDateError = submitAttempted &&
    (errors.includes('stay.from') || errors.includes('stay.to') || errors.includes('Invalid date range'));
  const isInvalidRange = submitAttempted && errors.includes('Invalid date range');
  return (
    <div className="flex h-full flex-col gap-1.5">
      <span className={labelClass}>
        <FormattedMessage id="form.label.checkInOut" />
        <span style={{ color: 'var(--owner-danger)', marginLeft: '2px' }}>*</span>
      </span>
      <div className="mt-auto">
        {editable ? (
          <DateRangePicker
            isOutsideRange={day => day.isBefore(moment(), 'day')}
            startDate={get(data, 'stay.from') ? moment(get(data, 'stay.from'), 'DD MMM YYYY') : null}
            endDate={get(data, 'stay.to') ? moment(get(data, 'stay.to'), 'DD MMM YYYY') : null}
            onChange={onChange}
          />
        ) : (
          <span style={{ color: 'var(--owner-text)', fontSize: '14px', lineHeight: '22px' }}>
            {`${get(data, 'stay.from', '')} ${get(data, 'stay.to') ? `→ ${get(data, 'stay.to')}` : ''}`}
          </span>
        )}
      </div>
      {hasDateError && (
        <span className={errorTextClass}>
          <FormattedMessage id={`form.error.${isInvalidRange ? 'invalidDate' : 'required'}.message`} />
        </span>
      )}
    </div>
  );
}

function OtherRequestsField({ data, editable, onChange }) {
  const { formatMessage } = useIntl();
  return (
    <div className="flex flex-col gap-1.5 md:col-span-2">
      <span className={labelClass}>{formatMessage({ id: 'form.label.otherRequests' })}</span>
      {editable ? (
        <TextArea
          value={get(data, 'otherRequests', '')}
          onChange={(event, eventData) => onChange('otherRequests', eventData.value)}
          style={{ minHeight: '110px' }}
        />
      ) : (
        <span style={{ color: 'var(--owner-text)', fontSize: '14px', lineHeight: '22px', whiteSpace: 'pre-wrap' }}>
          {get(data, 'otherRequests', '')}
        </span>
      )}
    </div>
  );
}
export class RequestBookingForm extends PureComponent {
  static propTypes = {
    allowCleaningOption: PropTypes.bool.isRequired,
    tenantId: PropTypes.string.isRequired,
    bedConfigs: PropTypes.array,
    beddingRooms: PropTypes.array,
    roomExcluded: PropTypes.bool,
    data: PropTypes.object,
    errors: PropTypes.array,
    roomNames: PropTypes.array,
    onBack: PropTypes.func,
    onChange: PropTypes.func,
    onSubmit: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
  };

  static defaultProps = {
    bedConfigs: [],
    beddingRooms: [],
    data: {},
    errors: [],
    roomNames: [],
  };

  state = {
    submitAttempted: false,
  };

  handleDateChange = (startDate, endDate) => {
    if (typeof this.props.onChange === 'function') {
      const startStr = startDate ? momentJpn(startDate).format('DD MMM YYYY') : '';
      const endStr = endDate ? momentJpn(endDate).format('DD MMM YYYY') : '';
      this.props.onChange('stay.from', startStr);
      this.props.onChange('stay.to', endStr);
    }
  };

  handleBack = event => {
    event.preventDefault();
    if (typeof this.props.onBack === 'function') {
      this.props.onBack();
    }
  };

  handleChange = (key, value) => {
    if (typeof this.props.onChange === 'function') {
      this.props.onChange(key, value);
    }
  };

  handleSubmit = event => {
    event.preventDefault();
    this.setState({ submitAttempted: true });
    this.props.onSubmit();
  };

  get editable() {
    return typeof this.props.onChange === 'function';
  }

  getOptions(textArray, config) {
    const { formatMessage } = this.props.intl;
    let textValues = textArray;
    if (config && config.shouldFormatMessage) {
      textValues = textArray.map(id => formatMessage({ id: `form.requestBooking.${id}` }));
    }
    const options = textValues.map(text => ({ text, key: text, value: text }));
    if (config && config.includeDecideLater) {
      const decideLater = formatMessage({ id: 'form.requestBooking.decideLater', defaultMessage: "I'll decide later" });
      if (!textValues.includes(decideLater)) {
        options.push({ text: decideLater, key: decideLater, value: decideLater });
      }
    }
    if (config && config.includeDefault && !textValues.includes('Default')) {
      const defaultOpt = formatMessage({ id: 'form.requestBooking.default' });
      options.push({ text: defaultOpt, key: defaultOpt, value: defaultOpt });
    }
    return options;
  }

  hasError = key => {
    return this.state.submitAttempted && this.props.errors.includes(key);
  };

  render() {
    const { allowCleaningOption, bedConfigs, beddingRooms, data, errors, roomNames, tenantId, roomExcluded } = this.props;
    const { submitAttempted } = this.state;
    const { editable } = this;
    const fieldProps = { data, editable, hasError: this.hasError, onChange: this.handleChange };

    const roomOptions = this.getOptions(roomNames);
    const bedConfigOptions = this.getOptions(bedConfigs, { includeDecideLater: true });
    const cleaningOptions = this.getOptions(CLEANING_OPTIONS, { shouldFormatMessage: true, includeDecideLater: true, includeDefault: true });
    const paymentOptions = this.getOptions(PAYMENT_OPTIONS, { shouldFormatMessage: true });
    const storageKeyOptions = this.getOptions(STORAGE_KEY_OPTIONS, { shouldFormatMessage: true });
    const serviceOptions = this.getOptions(SERVICE_OPTIONS, { shouldFormatMessage: true });

    const hasErrors = submitAttempted && errors.length > 0;

    return (
      <Form style={{ margin: '0 auto', maxWidth: '1110px', padding: '4px' }}>
        {roomExcluded && (
          <p role="alert" className={errorTextClass}>
            <FormattedMessage
              id="form.requestBooking.roomExcluded"
              defaultMessage="This unit is no longer available. Please select an available unit."
            />
          </p>
        )}
        <div className="grid grid-cols-1 gap-x-7 gap-y-5 md:grid-cols-2">
          <DropdownField
            {...fieldProps}
            keyName="room"
            options={roomOptions}
            required
            note={`form.requestBooking.notes.${tenantId}.interconnectedApartments`}
          />

          <DatesField data={data} errors={errors} submitAttempted={submitAttempted} editable={editable} onChange={this.handleDateChange} />

          <GuestsField {...fieldProps} />

          {editable && beddingRooms.length > 0 ? beddingRooms.map(room => (
            <BeddingRoom
              key={room.pmsRoomId}
              room={room}
              options={this.getOptions(room.bedConfigs, { includeDecideLater: true })}
              onChange={this.handleChange}
            />
          )) : (
            <DropdownField {...fieldProps} keyName="bedConfiguration" options={bedConfigOptions} />
          )}

          {allowCleaningOption && <DropdownField {...fieldProps} keyName="cleaning" options={cleaningOptions} />}

          <DropdownField {...fieldProps} keyName="ownerStorageKey" options={storageKeyOptions} />

          <DropdownField {...fieldProps} keyName="guestServices" options={serviceOptions} multiple />

          <DropdownField {...fieldProps} keyName="paymentMethod" options={paymentOptions} required />

          <OtherRequestsField data={data} editable={editable} onChange={this.handleChange} />

          <div className="mt-2 flex flex-col gap-2 md:col-span-2 md:items-end">
            {hasErrors && (
              <span className="text-sm leading-5 text-[var(--owner-danger)]">
                <FormattedMessage id="form.error.required.title" />
              </span>
            )}
            <div className="flex flex-wrap items-center gap-3">
              {typeof this.props.onBack === 'function' && (
                <ThemeButton
                  titleId="general.back"
                  onClick={this.handleBack}
                  style={backButtonStyle}
                />
              )}
              <ThemeButton
                titleId="general.submit"
                onClick={this.handleSubmit}
                style={submitButtonStyle}
              />
            </div>
          </div>
        </div>
      </Form>
    );
  }
}

const RequestBookingFormWithIntl = props => {
  const intl = useIntl();
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <RequestBookingForm {...props} intl={intl} />;
};

export default RequestBookingFormWithIntl;
